import { useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import { TagIcon } from '@phosphor-icons/react';
import type { SystemEntity } from '@erp/shared-types';
import type { TaskResponse, TaskTypeResponse } from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { toast } from 'src/components/ui/toast';
import { getEntityDisplayName } from 'src/components/system-entities';
import { useTaskTypes } from 'src/api/rpc/hooks/tasks/useTaskTypes';
import {
  useConvertTaskToType,
  useOpenTasksForAnchor,
} from 'src/api/rpc/hooks/tasks/useTaskDetail';
import {
  NO_PARENT_VALUE,
  buildConvertTaskInput,
  coerceTaskInputFieldValue,
  deriveSetTaskTypeForm,
  formatParentCandidateLabel,
  readSourceEntity,
  type AnchorHint,
  type SetTaskTypeFormState,
  type TaskSourceEntity,
} from './setTaskTypeForm';

/**
 * State + handlers behind the Set Type dialog. Kept out of the component so
 * the JSX stays declarative; the derivations themselves live in
 * `setTaskTypeForm.ts` (unit-tested without DOM).
 */
function useSetTaskTypeDialog(task: TaskResponse) {
  const gt = useGT();
  const { data } = useTaskTypes();
  const taskTypes: TaskTypeResponse[] = data ?? [];
  const convert = useConvertTaskToType(task.id);
  const [open, setOpen] = useState(false);
  const [taskTypeId, setTaskTypeId] = useState<string>('');
  // null = untouched, so the detected source entity keeps applying until the
  // user edits the field (or picks a different type, which resets it).
  const [anchorIdInput, setAnchorIdInput] = useState<string | null>(null);
  const [parentTaskId, setParentTaskId] = useState<string>(NO_PARENT_VALUE);
  const [inputValues, setInputValues] = useState<Record<string, string>>({});

  const sourceEntity = readSourceEntity(task.metadata);
  const selectedType = taskTypes.find((t) => t.id === taskTypeId);
  const form = deriveSetTaskTypeForm({
    selectedType,
    sourceEntity,
    anchorIdInput,
    inputValues,
  });

  const { tasks: anchorSiblings } = useOpenTasksForAnchor(
    form.declaredAnchorType && form.anchorIdValid
      ? { entityType: form.declaredAnchorType, entityId: form.anchorEntityId }
      : null,
  );
  const parentCandidates = anchorSiblings.filter((t) => t.id !== task.id);

  function resetFields(nextTypeId: string) {
    setTaskTypeId(nextTypeId);
    setAnchorIdInput(null);
    setParentTaskId(NO_PARENT_VALUE);
    setInputValues({});
  }

  function openDialog() {
    resetFields('');
    setOpen(true);
  }

  async function confirm() {
    if (!selectedType || !form.canSubmit) return;
    try {
      await convert.mutateAsync(
        buildConvertTaskInput({
          taskId: task.id,
          taskTypeId: selectedType.id,
          form,
          parentTaskId,
          inputValues,
        }),
      );
      toast.success(
        gt('Task type set to {name}', {
          context: 'toast after assigning a type to an untyped task',
          name: selectedType.name,
        }),
      );
      setOpen(false);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : gt('Failed to set task type', {
              context: 'toast after assigning a task type failed',
            }),
      );
    }
  }

  return {
    open,
    openDialog,
    closeDialog: () => setOpen(false),
    busy: convert.isPending,
    taskTypes,
    taskTypeId,
    selectedType,
    onTypeChange: resetFields,
    form,
    sourceEntity,
    onAnchorIdChange: setAnchorIdInput,
    parentCandidates,
    parentTaskId,
    onParentChange: setParentTaskId,
    inputValues,
    onInputValueChange: (fieldName: string, value: string) =>
      setInputValues((current) => ({ ...current, [fieldName]: value })),
    confirm,
  };
}

/**
 * "Set Type" for an untyped task: pick a type from the registry; when the type
 * declares an anchor, supply the entity id (pre-filled from the task's source
 * entity when the types agree) and optionally attach to an existing open
 * parent on the same anchor. One-way — typed tasks don't show this control.
 */
export function SetTaskTypeDialog({ task }: { task: TaskResponse }) {
  const {
    open,
    openDialog,
    closeDialog,
    busy,
    taskTypes,
    taskTypeId,
    selectedType,
    onTypeChange,
    form,
    sourceEntity,
    onAnchorIdChange,
    parentCandidates,
    parentTaskId,
    onParentChange,
    inputValues,
    onInputValueChange,
    confirm,
  } = useSetTaskTypeDialog(task);

  return (
    <>
      <Button size="sm" variant="outline" onClick={openDialog}>
        <TagIcon className="size-3.5" />
        <T context="button, give an untyped task a task type">Set Type</T>
      </Button>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) closeDialog();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              <T context="dialog title, give an untyped task a task type">
                Set task type
              </T>
            </DialogTitle>
            <DialogDescription>
              <T context="explains that setting a task type is permanent">
                The type cannot be changed once set. If the type anchors to a
                business record, the record id is required.
              </T>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <TaskTypeField
              taskId={task.id}
              taskTypes={taskTypes}
              value={taskTypeId}
              onChange={onTypeChange}
              disabled={busy}
            />
            {selectedType && !form.declaredAnchorType && (
              <p className="text-[11px] text-muted-foreground">
                <T context="note when the chosen task type has no anchor">
                  This type is not anchored to a business record.
                </T>
              </p>
            )}
            {form.declaredAnchorType && (
              <AnchorIdField
                taskId={task.id}
                anchorType={form.declaredAnchorType}
                value={form.anchorEntityId}
                invalid={form.anchorEntityId.length > 0 && !form.anchorIdValid}
                hint={form.anchorHint}
                sourceEntity={sourceEntity}
                onChange={onAnchorIdChange}
                disabled={busy}
              />
            )}
            {form.declaredAnchorType && parentCandidates.length > 0 && (
              <ParentTaskField
                taskId={task.id}
                candidates={parentCandidates}
                value={parentTaskId}
                onChange={onParentChange}
                disabled={busy}
              />
            )}
            {form.inputFields.length > 0 && (
              <TaskTypeInputFields
                taskId={task.id}
                fields={form.inputFields}
                values={inputValues}
                onChange={onInputValueChange}
                disabled={busy}
              />
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" disabled={busy} onClick={closeDialog}>
              <T context="button, dismiss set task type dialog">Back</T>
            </Button>
            <Button
              disabled={busy || !form.canSubmit}
              onClick={() => void confirm()}
            >
              {busy ? (
                <T context="button label while saving task type">Saving…</T>
              ) : (
                <T context="button, confirm setting the task type">Set Type</T>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function TaskTypeField({
  taskId,
  taskTypes,
  value,
  onChange,
  disabled,
}: {
  taskId: string;
  taskTypes: TaskTypeResponse[];
  value: string;
  onChange: (taskTypeId: string) => void;
  disabled: boolean;
}) {
  const gt = useGT();
  return (
    <div className="space-y-1">
      <Label htmlFor={`task-${taskId}-type`} className="text-[11px]">
        <T context="label for task type picker">Task type</T>
      </Label>
      <Select
        value={value || undefined}
        onValueChange={onChange}
        disabled={disabled}
      >
        <SelectTrigger id={`task-${taskId}-type`} className="h-8">
          <SelectValue
            placeholder={gt('Choose a type…', {
              context: 'placeholder for task type picker',
            })}
          />
        </SelectTrigger>
        <SelectContent>
          {taskTypes.map((type) => (
            <SelectItem key={type.id} value={type.id}>
              {type.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function AnchorIdField({
  taskId,
  anchorType,
  value,
  invalid,
  hint,
  sourceEntity,
  onChange,
  disabled,
}: {
  taskId: string;
  anchorType: SystemEntity;
  value: string;
  invalid: boolean;
  hint: AnchorHint;
  sourceEntity: TaskSourceEntity | null;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  const gt = useGT();
  return (
    <div className="space-y-1">
      <Label htmlFor={`task-${taskId}-anchor`} className="text-[11px]">
        <T context="label for anchored record id; {entity} is the record type, e.g. AP Invoice">
          <Var>{getEntityDisplayName(anchorType)}</Var> id
        </T>
      </Label>
      <Input
        id={`task-${taskId}-anchor`}
        className="h-8 font-mono text-xs"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        placeholder={gt('UUID of the record this task is about', {
          context: 'placeholder for anchor entity id input',
        })}
        aria-invalid={invalid}
      />
      <AnchorHintText hint={hint} sourceEntity={sourceEntity} />
    </div>
  );
}

function AnchorHintText({
  hint,
  sourceEntity,
}: {
  hint: AnchorHint;
  sourceEntity: TaskSourceEntity | null;
}) {
  if (hint === 'detected') {
    return (
      <p className="text-[11px] text-muted-foreground">
        <T context="hint that the anchor id was pre-filled from the task's source entity">
          Detected from the task's source entity.
        </T>
      </p>
    );
  }
  if (hint === 'mismatch' && sourceEntity) {
    return (
      <p className="text-[11px] text-amber-700">
        <T context="warning that the task's source entity type differs from the chosen type's anchor">
          This task was raised for a{' '}
          <Var>{getEntityDisplayName(sourceEntity.entityType)}</Var>, which does
          not match this type's anchor.
        </T>
      </p>
    );
  }
  return null;
}

const EMPTY_ENUM_VALUE = '__empty_enum__';

function taskInputPlaceholder(
  field: SetTaskTypeFormState['inputFields'][number],
  gt: ReturnType<typeof useGT>,
): string {
  if (field.isMultiple || field.fieldType === 'array') {
    return gt('JSON array', {
      context: 'placeholder for multi-value task input',
    });
  }
  if (field.fieldType === 'boolean') {
    return gt('true or false', {
      context: 'placeholder for boolean task input',
    });
  }
  return field.label;
}

function TaskTypeEnumInput({
  inputId,
  field,
  raw,
  invalid,
  disabled,
  onChange,
}: {
  inputId: string;
  field: SetTaskTypeFormState['inputFields'][number];
  raw: string;
  invalid: boolean;
  disabled: boolean;
  onChange: (fieldName: string, value: string) => void;
}) {
  const gt = useGT();
  const enumValues = field.enumValues ?? [];
  return (
    <Select
      value={field.isRequired ? raw || null : raw || EMPTY_ENUM_VALUE}
      onValueChange={(next) =>
        onChange(field.fieldName, next === EMPTY_ENUM_VALUE ? '' : next)
      }
      disabled={disabled}
    >
      <SelectTrigger
        id={inputId}
        className="h-8 text-xs"
        aria-required={field.isRequired}
        aria-invalid={invalid}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {!field.isRequired && (
          <SelectItem value={EMPTY_ENUM_VALUE}>
            {gt('None', {
              context: 'option leaving an optional enum task input unset',
            })}
          </SelectItem>
        )}
        {enumValues.map((option) => (
          <SelectItem key={option} value={option}>
            {option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function TaskTypeInputField({
  taskId,
  field,
  raw,
  onChange,
  disabled,
}: {
  taskId: string;
  field: SetTaskTypeFormState['inputFields'][number];
  raw: string;
  onChange: (fieldName: string, value: string) => void;
  disabled: boolean;
}) {
  const gt = useGT();
  const inputId = `task-${taskId}-input-${field.fieldName}`;
  const invalid =
    raw.trim().length > 0 && !coerceTaskInputFieldValue(field, raw).ok;
  const useEnum = (field.enumValues?.length ?? 0) > 0 && !field.isMultiple;
  return (
    <div className="space-y-1">
      <Label htmlFor={inputId} className="text-[11px]">
        {field.label}
        {field.isRequired ? ' *' : ''}
      </Label>
      {useEnum ? (
        <TaskTypeEnumInput
          inputId={inputId}
          field={field}
          raw={raw}
          invalid={invalid}
          disabled={disabled}
          onChange={onChange}
        />
      ) : (
        <Input
          id={inputId}
          className="h-8 text-xs"
          aria-required={field.isRequired}
          value={raw}
          disabled={disabled}
          onChange={(event) => onChange(field.fieldName, event.target.value)}
          placeholder={taskInputPlaceholder(field, gt)}
          aria-invalid={invalid}
        />
      )}
    </div>
  );
}

function TaskTypeInputFields({
  taskId,
  fields,
  values,
  onChange,
  disabled,
}: {
  taskId: string;
  fields: SetTaskTypeFormState['inputFields'];
  values: Record<string, string>;
  onChange: (fieldName: string, value: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="space-y-2">
      <p className="text-[11px] font-medium text-muted-foreground">
        <T context="heading for typed task input fields on set type">
          Task input
        </T>
      </p>
      {fields.map((field) => (
        <TaskTypeInputField
          key={field.fieldName}
          taskId={taskId}
          field={field}
          raw={values[field.fieldName] ?? ''}
          onChange={onChange}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

function ParentTaskField({
  taskId,
  candidates,
  value,
  onChange,
  disabled,
}: {
  taskId: string;
  candidates: TaskResponse[];
  value: string;
  onChange: (parentTaskId: string) => void;
  disabled: boolean;
}) {
  const gt = useGT();
  return (
    <div className="space-y-1">
      <Label htmlFor={`task-${taskId}-parent`} className="text-[11px]">
        <T context="label for optional parent task picker">Parent task</T>
      </Label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger id={`task-${taskId}-parent`} className="h-8">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NO_PARENT_VALUE}>
            {gt('None', {
              context: 'option leaving the task without a parent',
            })}
          </SelectItem>
          {candidates.map((candidate) => (
            <SelectItem key={candidate.id} value={candidate.id}>
              {formatParentCandidateLabel(candidate)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-[11px] text-muted-foreground">
        <T context="hint under the parent task picker">
          Open tasks on the same record. A parent is never created for you.
        </T>
      </p>
    </div>
  );
}
